import isEmpty from 'lodash/isEmpty'
import {
  PivotValueFilterDirectionEnum,
  PivotValueFilterModeEnum,
  PivotValueFilterScopeEnum,
} from '@ztechapps/common.contract'
import type { PivotLayout, PivotLayoutFilter } from '@ztechapps/common.contract'
import { isObject } from '../is-object'
import { isPivotCalculatedMeasure } from '../is-pivot-calculated-measure'

/**
 * Finds the first rule a filter entry's value filter breaks against the layout's Rows, Columns and aggregated
 * values, or undefined when the entry holds none or it is valid. Reads untrusted model-written layouts, so every
 * level is checked before it is read.
 */
export const findPivotValueFilterProblem = (
  layout: Pick<PivotLayout, 'rows' | 'columns' | 'measures'>,
  filter: PivotLayoutFilter
): string | undefined => {
  const { valueFilter } = filter
  if (valueFilter === undefined) return undefined
  const name = String(filter.field)
  if (!isObject(valueFilter)) return `${name} has a value filter that is not an object.`
  const isSameItem = (item: unknown): boolean =>
    isObject(item) && item.field === filter.field && item.dateGroup === filter.dateGroup
  const rowIndex = layout.rows.findIndex(isSameItem)
  const columnIndex = layout.columns.findIndex(isSameItem)
  if (rowIndex === -1 && columnIndex === -1)
    return `${name} has a value filter but is not in Rows or Columns — only a Rows or Columns field takes a Top or Bottom N.`
  if (!isEmpty(filter.includedValues) || !isEmpty(filter.excludedValues))
    return `${name} has a value filter and a member selection — a field takes only one of them.`
  const { measure, direction, mode, value, scope } = valueFilter as Record<string, unknown>
  const ranked = isObject(measure) ? measure : {}
  const isAggregatedMeasure = layout.measures.some(
    (item) =>
      isObject(item) &&
      !isPivotCalculatedMeasure(item) &&
      item.field === ranked.field &&
      item.aggregation === ranked.aggregation
  )
  if (!isAggregatedMeasure)
    return `${name} ranks by ${String(ranked.field)} (${String(ranked.aggregation)}), which is not an aggregated value of this layout.`
  if (!Object.values(PivotValueFilterDirectionEnum).includes(direction as PivotValueFilterDirectionEnum))
    return `${name} ranks ${String(direction)}, which is not a top or bottom direction.`
  if (!Object.values(PivotValueFilterModeEnum).includes(mode as PivotValueFilterModeEnum))
    return `${name} counts by ${String(mode)}, which is not an items or percent mode.`
  if (!Object.values(PivotValueFilterScopeEnum).includes(scope as PivotValueFilterScopeEnum))
    return `${name} ranks ${String(scope)}, which is not an overall or perParent scope.`
  if (typeof value !== 'number' || !Number.isInteger(value) || value < 1)
    return `${name} keeps ${String(value)} members, which is not a positive whole number.`
  if (mode === PivotValueFilterModeEnum.percent && value > 100)
    return `${name} keeps ${value} percent of its members, which is more than 100.`
  // the first field of an axis has no outer field to rank within
  if (scope === PivotValueFilterScopeEnum.perParent && (rowIndex === 0 || (rowIndex === -1 && columnIndex === 0)))
    return `${name} is the first field of its axis, so it has no outer field to rank within.`
  return undefined
}
